import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Typography, Box,
    Chip, Alert, Collapse, IconButton,
    Tabs, Tab
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import {InputField} from '@/pages/components/common/InputField';
import {
    buildRoundingApplyEntries,
    computeRemainingDifference,
    isRoundingDraftBalanced,
    roundingNodeKey,
    type RoundingApplyEntry,
} from '@/pages/components/fs-report/roundingAdjustHelpers';

/**
 * 目标节点信息
 */
export interface RoundingTargetNode {
    fsId: number;
    code: string;
    name: string;
    statement: 'BS' | 'PL';
    /** 当前 Round 后的值 */
    currentRoundedValue: number;
    /** 所属 Level 2 分组名称 */
    groupName: string;
    /** 所属 Level 2 父节点的 code */
    parentCode: string;
    /**
     * MULTI·PL 子期间 YOA；SINGLE / BS 为 null。
     */
    yearOfAssessment?: string | null;
}

/** Period Tab 切片（与 FsAdjustment PL Period Tabs 对齐） */
export interface RoundingPeriodSlice {
    key: string;
    yearOfAssessment: string;
    label?: string;
}

export interface RoundingAdjustDialogProps {
    open: boolean;
    onClose: () => void;
    /** 初始差值 rounded(BS-NL-RES) - rounded(BS-EQ) */
    difference: number;
    /** 可选节点列表（BS + PL Level 3 叶子） */
    targetNodes: RoundingTargetNode[];
    /** 用户确认后回调（保存全部非零节点） */
    onApply: (entries: RoundingApplyEntry[]) => void;
    /** 已保存的节点调整量（回显用；键为 roundingNodeKey） */
    savedNodeValues?: Record<string, number> | null;
    /**
     * MULTI 时传入 ≥2 个子期间；PL tab 下展示 Period Tabs。
     * SINGLE / 不传：不渲染第二行 Tab（#20）。
     */
    periods?: RoundingPeriodSlice[] | null;
}

/**
 * 四舍五入差值调整弹窗
 * 用户在此选择将 ±1 差值分配到哪个 Level 3 叶子节点
 */
export const RoundingAdjustDialog: React.FC<RoundingAdjustDialogProps> = ({
    open,
    onClose,
    difference,
    targetNodes,
    onApply,
    savedNodeValues = null,
    periods = null,
}) => {
    const [search, setSearch] = useState('');
    const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
    const [viewMode, setViewMode] = useState<'simple' | 'full'>('simple');
    const [activeTab, setActiveTab] = useState<'BS' | 'PL'>('BS');
    const [nodeValues, setNodeValues] = useState<Record<string, number>>({});
    const [plPeriodKey, setPlPeriodKey] = useState('');

    const isPlMulti = Array.isArray(periods) && periods.length >= 2;

    useEffect(() => {
        if (!open) {
            setNodeValues({});
            setSearch('');
            setActiveTab('BS');
            setPlPeriodKey('');
        }
    }, [open]);

    useEffect(() => {
        if (!open || !isPlMulti || !periods?.length) return;
        if (!plPeriodKey || !periods.some(p => p.key === plPeriodKey)) {
            setPlPeriodKey(periods[0].key);
        }
    }, [open, isPlMulti, periods, plPeriodKey]);

    const activePlYoa = useMemo(() => {
        if (!isPlMulti || !periods?.length) return null;
        const slice = periods.find(p => p.key === plPeriodKey) || periods[0];
        return String(slice?.yearOfAssessment || '').trim() || null;
    }, [isPlMulti, periods, plPeriodKey]);

    const showWarning = Math.abs(difference) > 1;

    const remainingDifference = useMemo(
        () => computeRemainingDifference(difference, nodeValues, targetNodes),
        [difference, nodeValues, targetNodes]
    );

    const isBalanced = useMemo(
        () => isRoundingDraftBalanced(difference, nodeValues, targetNodes),
        [difference, nodeValues, targetNodes]
    );

    const visibleNodes = useMemo(() => {
        if (viewMode === 'simple') {
            return targetNodes.filter(n => n.currentRoundedValue !== 0);
        }
        return targetNodes;
    }, [targetNodes, viewMode]);

    const tabFilteredNodes = useMemo(() => {
        return visibleNodes.filter(n => {
            if (n.statement !== activeTab) return false;
            if (activeTab === 'PL' && isPlMulti && activePlYoa) {
                const nodeYoa = String(n.yearOfAssessment || '').trim();
                return nodeYoa === activePlYoa;
            }
            return true;
        });
    }, [visibleNodes, activeTab, isPlMulti, activePlYoa]);

    const grouped = useMemo(() => {
        const keyword = search.trim().toLowerCase();
        const filtered = keyword
            ? tabFilteredNodes.filter(n =>
                n.name.toLowerCase().includes(keyword) ||
                n.code.toLowerCase().includes(keyword) ||
                n.groupName.toLowerCase().includes(keyword)
            )
            : tabFilteredNodes;

        const groups: Record<string, RoundingTargetNode[]> = {};
        for (const node of filtered) {
            const key = node.groupName || 'Other';
            if (!groups[key]) groups[key] = [];
            groups[key].push(node);
        }
        return groups;
    }, [tabFilteredNodes, search]);

    const hasNodes = tabFilteredNodes.length > 0;
    const hasAllNodes = targetNodes.length > 0;

    const bsCount = useMemo(() => visibleNodes.filter(n => n.statement === 'BS').length, [visibleNodes]);
    const plCount = useMemo(() => visibleNodes.filter(n => n.statement === 'PL').length, [visibleNodes]);

    useEffect(() => {
        const keyword = search.trim();
        const next: Record<string, boolean> = {};
        Object.entries(grouped).forEach(([groupKey, nodes]) => {
            if (keyword) {
                next[groupKey] = true;
            } else if (nodes.some(n => {
                const k = isPlMulti ? roundingNodeKey(n) : n.code;
                return (nodeValues[k] || 0) !== 0;
            })) {
                next[groupKey] = true;
            } else {
                next[groupKey] = expandedGroups[groupKey] ?? false;
            }
        });
        setExpandedGroups(next);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [grouped, search, nodeValues, isPlMulti]);

    const toggleGroup = (groupKey: string) => {
        setExpandedGroups(prev => ({...prev, [groupKey]: !prev[groupKey]}));
    };

    const hasAssigned = useMemo(() => {
        return Object.values(nodeValues).some(v => v !== 0);
    }, [nodeValues]);

    const resolveNodeKey = useCallback((node: RoundingTargetNode) => {
        // SINGLE：与改造前一致用 code；MULTI·PL 用 code@YOA
        return isPlMulti ? roundingNodeKey(node) : node.code;
    }, [isPlMulti]);

    const handleIncrement = useCallback((key: string) => {
        setNodeValues(prev => ({...prev, [key]: (prev[key] || 0) + 1}));
    }, []);

    const handleDecrement = useCallback((key: string) => {
        setNodeValues(prev => ({...prev, [key]: (prev[key] || 0) - 1}));
    }, []);

    /** 已平衡时：仅有数值的节点可调整——正值允许减、负值允许加，零值节点全部禁用 */
    const canDecrement = useCallback((val: number) => !isBalanced || val > 0, [isBalanced]);
    const canIncrement = useCallback((val: number) => !isBalanced || val < 0, [isBalanced]);

    const handleApply = () => {
        if (!isBalanced || !hasAssigned) return;
        const entries = buildRoundingApplyEntries(nodeValues, targetNodes);
        if (entries.length === 0) return;
        onApply(entries);
    };

    useEffect(() => {
        if (!open || !savedNodeValues || Object.keys(savedNodeValues).length === 0) return;
        setNodeValues({...savedNodeValues});
    }, [open, savedNodeValues]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                <WarningAmberIcon color="warning"/>
                Rounding Adjustment
            </DialogTitle>

            <DialogContent dividers>
                <Alert severity={showWarning ? 'warning' : isBalanced ? 'success' : 'info'} sx={{mb: 2}}>
                    <Typography variant="body2">
                        In Audited (Round) mode, a mismatch was detected between{' '}
                        <strong>NET ASSETS</strong> and{' '}
                        <strong>CAPITAL AND RESERVES</strong>. Initial difference:{' '}
                        <strong>{difference > 0 ? `+${difference}` : difference}</strong>.
                        {showWarning && ' (Difference exceeds ±1, please verify data accuracy.)'}
                    </Typography>
                    <Typography variant="body2" sx={{mt: 0.5}}>
                        Adjust nodes using +/− until remaining difference is 0 before applying.
                        Remaining:{' '}
                        <strong>
                            {remainingDifference > 0 ? `+${remainingDifference}` : remainingDifference}
                        </strong>
                        {isBalanced && ' — balanced.'}
                    </Typography>
                </Alert>

                {hasAllNodes && (
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 2}}>
                        <InputField
                            placeholder="Search by node name or code..."
                            fullWidth
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            align="left"
                        />
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setViewMode(prev => prev === 'simple' ? 'full' : 'simple')}
                            startIcon={viewMode === 'simple' ? <ViewModuleIcon fontSize="small"/> : <ViewListIcon fontSize="small"/>}
                            sx={{textTransform: 'none', whiteSpace: 'nowrap', fontSize: 12, px: 1.5}}
                        >
                            {viewMode === 'simple' ? 'Full' : 'Simple'}
                        </Button>
                    </Box>
                )}

                {hasAllNodes && (
                    <Tabs
                        value={activeTab}
                        onChange={(_, v) => setActiveTab(v)}
                        sx={{mb: 1.5, minHeight: 36, '& .MuiTab-root': {minHeight: 36, py: 0.5, textTransform: 'none'}}}
                    >
                        <Tab value="BS" label={`Balance Sheet (${bsCount})`} />
                        <Tab value="PL" label={`Profit & Loss (${plCount})`} />
                    </Tabs>
                )}

                {hasAllNodes && activeTab === 'PL' && isPlMulti && periods && (
                    <Tabs
                        value={plPeriodKey || periods[0]?.key || false}
                        onChange={(_, v) => setPlPeriodKey(String(v))}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                            mb: 1.5,
                            minHeight: 32,
                            '& .MuiTab-root': {minHeight: 32, py: 0, px: 1.5, textTransform: 'none', fontSize: 13},
                        }}
                    >
                        {periods.map(s => (
                            <Tab
                                key={s.key}
                                value={s.key}
                                label={s.yearOfAssessment || s.label || s.key}
                            />
                        ))}
                    </Tabs>
                )}

                {!hasNodes ? (
                    <Typography color="text.secondary" sx={{py: 2, textAlign: 'center'}}>
                        {viewMode === 'simple'
                            ? 'No active nodes found. Switch to Full view to see all.'
                            : 'No available nodes.'}
                    </Typography>
                ) : (
                    <Box>
                        {Object.entries(grouped).map(([groupKey, nodes]) => {
                            const isExpanded = !!expandedGroups[groupKey];
                            const hasValueChild = nodes.some(n => (nodeValues[resolveNodeKey(n)] || 0) !== 0);
                            return (
                                <Box key={groupKey} sx={{
                                    mb: 1,
                                    border: '1px solid',
                                    borderColor: hasValueChild ? 'primary.light' : 'divider',
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                }}>
                                    <Box
                                        onClick={() => toggleGroup(groupKey)}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            px: 1.25,
                                            py: 0.75,
                                            cursor: 'pointer',
                                            backgroundColor: hasValueChild ? 'action.selected' : 'action.hover',
                                            '&:hover': {backgroundColor: 'action.selected'},
                                            userSelect: 'none',
                                        }}
                                    >
                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                            <Typography variant="subtitle2" fontWeight={600}>
                                                {groupKey}
                                            </Typography>
                                            <Chip label={nodes.length} size="small"
                                                  sx={{height: 18, '& .MuiChip-label': {px: 0.75, fontSize: 11}}}/>
                                        </Box>
                                        <IconButton size="small" sx={{p: 0.25}} aria-label={isExpanded ? 'collapse' : 'expand'}>
                                            {isExpanded ? <ExpandLessIcon fontSize="small"/> : <ExpandMoreIcon fontSize="small"/>}
                                        </IconButton>
                                    </Box>
                                    <Collapse in={isExpanded} unmountOnExit>
                                        <Box sx={{px: 1.5, py: 0.5}}>
                                            {nodes.map(node => {
                                                const key = resolveNodeKey(node);
                                                const val = nodeValues[key] || 0;
                                                return (
                                                    <Box
                                                        key={key}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            py: 0.5,
                                                            '&:not(:last-child)': {borderBottom: '1px solid', borderColor: 'divider'},
                                                        }}
                                                    >
                                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0}}>
                                                            <Typography variant="body2" noWrap>{node.name}</Typography>
                                                            <Chip label={node.code} size="small" variant="outlined"
                                                                  sx={{height: 18, '& .MuiChip-label': {px: 0.5, fontSize: 11}}}/>
                                                            <Typography variant="caption" color="text.secondary" noWrap>
                                                                ({node.currentRoundedValue})
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, ml: 1}}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleDecrement(key)}
                                                                disabled={!canDecrement(val)}
                                                                sx={{p: 0.25}}
                                                            >
                                                                <RemoveIcon fontSize="small"/>
                                                            </IconButton>
                                                            <Typography
                                                                variant="body2"
                                                                fontWeight={val !== 0 ? 700 : 400}
                                                                color={val > 0 ? 'success.main' : val < 0 ? 'error.main' : 'text.secondary'}
                                                                sx={{minWidth: 24, textAlign: 'center', userSelect: 'none'}}
                                                            >
                                                                {val > 0 ? `+${val}` : val}
                                                            </Typography>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleIncrement(key)}
                                                                disabled={!canIncrement(val)}
                                                                sx={{p: 0.25}}
                                                            >
                                                                <AddIcon fontSize="small"/>
                                                            </IconButton>
                                                        </Box>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    </Collapse>
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    disabled={!isBalanced || !hasAssigned}
                    onClick={handleApply}
                >
                    Apply
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RoundingAdjustDialog;
